/**
 * Company Context Banner
 * Shows which company and data source the user is currently working with
 * Essential for multi-tenant clarity
 */

import { Building2, Database, AlertCircle, Shield } from 'lucide-react';
import { useAuthStore } from '@store/authStore';
import { useDataSourceStore } from '@store/dataSourceStore';

interface CompanyContextBannerProps {
  showDataSource?: boolean;
  className?: string;
}

const CompanyContextBanner = ({
  showDataSource = true,
  className = '',
}: CompanyContextBannerProps) => {
  const user = useAuthStore(state => state.user);
  const { selectedCompany, selectedDataSources } = useDataSourceStore();

  const isSuperAdmin = user?.roles?.includes('super-admin') || user?.roles?.includes('super_admin');

  // For non-super-admin users, we show their company from user profile
  // For super-admin users, we show the selected company from store
  const displayCompany = isSuperAdmin ? selectedCompany?.name : user?.companyId?.name;
  const displayDataSource = selectedDataSources.length > 0 ? `${selectedDataSources.length} source(s) selected` : undefined;

  // If no company is selected/available, show warning
  if (!displayCompany && isSuperAdmin) {
    return (
      <div
        className={`bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 rounded-lg ${className}`}
      >
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
              No Company Selected
            </h4>
            <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-0.5">
              As a Super Admin, please select a company from the header to view its data.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-4 shadow-sm ${className}`}
    >
      <div className="flex items-center justify-between gap-4">
        {/* Company Info */}
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white dark:bg-secondary-800 rounded-lg shadow-sm">
            <Building2 className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wider">
                {isSuperAdmin ? 'Viewing Company' : 'Your Company'}
              </span>
              {isSuperAdmin && (
                <Shield
                  className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400"
                  aria-label="Super Admin"
                />
              )}
            </div>
            <h3 className="text-lg font-bold text-secondary-900 dark:text-white mt-0.5">
              {displayCompany || 'Unknown Company'}
            </h3>
          </div>
        </div>

        {/* Data Source Info */}
        {showDataSource && displayDataSource && (
          <>
            <div className="h-10 w-px bg-primary-300 dark:bg-primary-700 hidden md:block" />
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white dark:bg-secondary-800 rounded-lg shadow-sm">
                <Database className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider block">
                  Active Data Source
                </span>
                <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mt-0.5">
                  {displayDataSource}
                </h4>
              </div>
            </div>
          </>
        )}

        {/* Admin Badge */}
        {!isSuperAdmin && (
          <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-secondary-800 rounded-full shadow-sm">
            <Shield className="w-4 h-4 text-green-600 dark:text-green-400" />
            <span className="text-xs font-semibold text-green-700 dark:text-green-300">
              Company Admin
            </span>
          </div>
        )}
      </div>

      {/* Super Admin Note */}
      {isSuperAdmin && (
        <div className="mt-3 pt-3 border-t border-primary-200 dark:border-primary-800">
          <p className="text-xs text-primary-700 dark:text-primary-300 flex items-center gap-2">
            <Shield className="w-3.5 h-3.5" />
            <span>
              <strong>Super Admin Mode:</strong> You can switch companies using the selector in the
              header. Data shown below is scoped to the selected company.
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

export default CompanyContextBanner;
